import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Select } from '../components/Select';
import { Modal } from '../components/Modal';
import styles from './Conversations.module.scss';

const SORT_OPTIONS = [
  { value: 'human', label: 'Human chats' },
  { value: 'ai', label: 'AI chats' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'ban', label: 'Ban' },
];

function getLastMessageText(conv) {
  const last = conv?.lastMessage ?? conv?.lastMessageContent;
  if (last == null) return '—';
  if (typeof last === 'string') return last.trim() || '—';
  const content = last?.content ?? last;
  if (typeof content === 'string') return content.trim() || '—';
  if (content == null) return '—';
  const text = content?.text ?? content?.message ?? (typeof content?.content === 'string' ? content.content : null);
  if (text != null && String(text).trim() !== '') return String(text).trim();
  const fileList = content?.files ?? content?.file ?? content?.attachments ?? content?.attachment;
  const hasFiles = Array.isArray(fileList) ? fileList.length > 0 : fileList != null && typeof fileList === 'object';
  if (hasFiles) return '📎 File';
  return '—';
}

function isVisitorMessage(msg) {
  if (msg == null) return false;
  const role = (msg.role || msg.sender || msg.source || '').toLowerCase();
  if (role === 'visitor') return true;
  if (msg.fromVisitor === true) return true;
  return false;
}

function MessageBubble({ msg, styles: s }) {
  const c = msg?.content;

  let text = null;
  let files = [];

  if (c != null) {
    if (typeof c === 'string') {
      text = c;
    } else if (typeof c === 'object') {
      text =
        c.text ??
        c.message ??
        (typeof c.content === 'string' ? c.content : null);
      const fileList = c.files ?? c.file ?? c.attachments ?? c.attachment;
      if (Array.isArray(fileList)) {
        files = fileList;
      } else if (fileList != null && typeof fileList === 'object') {
        files = [fileList];
      }
    }
  }

  const hasText = text != null && String(text).trim() !== '';
  const hasFiles = files.length > 0;

  if (!hasText && !hasFiles) {
    return (
      <span className={s.messageContent}>—</span>
    );
  }

  return (
    <div className={s.messageBubbleInner}>
      {hasText && (
        <span className={s.messageContent}>{String(text).trim()}</span>
      )}
      {hasFiles && (
        <div className={s.messageFiles}>
          {files.map((file, idx) => {
            const url = file.url ?? file.src ?? file.link;
            const name = file.name ?? file.filename ?? file.title ?? `File ${idx + 1}`;
            const isImage =
              (file.type || file.mimeType || '').startsWith('image/') ||
              /\.(jpe?g|png|gif|webp)$/i.test(name);
            if (!url) return <span key={idx} className={s.messageFileLabel}>{name}</span>;
            return (
              <div key={idx} className={s.messageFile}>
                {isImage ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className={s.messageFileLink}>
                    <img src={url} alt={name} className={s.messageFileImage} />
                  </a>
                ) : (
                  <a href={url} target="_blank" rel="noopener noreferrer" className={s.messageFileLink}>
                    📎 {name}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'open') return styles.statusOpen;
  if (s === 'resolved' || s === 'closed') return styles.statusResolved;
  if (s === 'handling' || s === 'assigned') return styles.statusHandling;
  return styles.statusDefault;
}

export const ConversationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || '';

  const [conversations, setConversations] = useState([]);
  const [sort, setSort] = useState('human');
  const [statusFilter, setStatusFilter] = useState('open');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [messagesModalConversationId, setMessagesModalConversationId] =
    useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setConversations([]);
      return;
    }

    const fetchConversations = async () => {
      try {
        setIsLoading(true);
        setError('');
        const { data } = await api.post(`/admin/conversations/${userId}`, {
          filter: {},
        });
        setConversations(
          Array.isArray(data?.conversations) ? data.conversations : []
        );
      } catch (err) {
        setError(
          err.response?.data?.message || 'Failed to load conversations'
        );
        setConversations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversations();
  }, [userId]);

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (sort === 'human') {
      list = list.filter(c => c.aiEnabled === false);
    } else if (sort === 'ai') {
      list = list.filter(c => c.aiEnabled === true);
    }
    const status = (s) => (s != null ? String(s).toLowerCase() : '');
    if (statusFilter === 'open') {
      list = list.filter(c => status(c.status) === 'open');
    } else if (statusFilter === 'resolved') {
      list = list.filter(c => status(c.status) === 'resolved');
    } else if (statusFilter === 'unresolved') {
      list = list.filter(c => status(c.status) === 'unresolved');
    } else if (statusFilter === 'ban') {
      list = list.filter(c => status(c.status) === 'ban');
    }
    return list;
  }, [conversations, sort, statusFilter]);

  useEffect(() => {
    if (!messagesModalConversationId) {
      setMessages([]);
      return;
    }
    const fetchMessages = async () => {
      try {
        setMessagesLoading(true);
        const { data } = await api.get(
          `/admin/messages/${messagesModalConversationId}`
        );
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch {
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    };
    fetchMessages();
  }, [messagesModalConversationId]);

  const handleBubbleClick = useCallback(convId => {
    setMessagesModalConversationId(convId);
  }, []);

  const handleCloseMessagesModal = useCallback(() => {
    setMessagesModalConversationId(null);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => navigate('/admin/dashboard')}
        >
          ← Back to dashboard
        </button>

        <h1 className={styles.header}>Conversations</h1>

        {!userId && (
          <p className={styles.error}>
            No user specified. Please return and select a user.
          </p>
        )}

        {userId && (
          <div className={styles.filtersRow}>
            <div className={styles.sortRow}>
              <p className={styles.sortLabel}>Sort</p>
              <Select
                value={sort}
                onChange={e => setSort(e.target.value)}
                options={SORT_OPTIONS}
                placeholder="Select"
              />
            </div>
            <div className={styles.sortRow}>
              <p className={styles.sortLabel}>Status</p>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                options={STATUS_OPTIONS}
                placeholder="Select"
              />
            </div>
          </div>
        )}

        {userId && isLoading && (
          <p className={styles.empty}>Loading conversations...</p>
        )}

        {userId && !isLoading && error && (
          <p className={styles.error}>{error}</p>
        )}

        {userId && !isLoading && !error && filteredConversations.length === 0 && (
          <p className={styles.empty}>
            No {sort === 'human' ? 'human' : 'AI'} conversations
            {statusFilter ? ` with status "${statusFilter}"` : ''} found.
          </p>
        )}

        {userId && !isLoading && !error && filteredConversations.length > 0 && (
          <ul className={styles.list}>
            {filteredConversations.map(conv => (
              <li
                key={conv._id}
                className={`${styles.item} ${styles.bubble} ${getStatusClass(conv.status)}`}
                role="button"
                tabIndex={0}
                onClick={() => handleBubbleClick(conv._id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBubbleClick(conv._id);
                  }
                }}
              >
                <p className={styles.lastMessage}>
                  {getLastMessageText(conv)}
                </p>
                <span className={styles.statusBadge}>
                  {conv.status != null && conv.status !== ''
                    ? conv.status
                    : '—'}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Modal
          isOpen={Boolean(messagesModalConversationId)}
          title="Messages"
          onClose={handleCloseMessagesModal}
          onConfirm={handleCloseMessagesModal}
          confirmLabel="Close"
        >
          {messagesLoading ? (
            <p className={styles.modalEmpty}>Loading messages...</p>
          ) : messages.length === 0 ? (
            <p className={styles.modalEmpty}>No messages in this conversation.</p>
          ) : (
            <ul className={styles.messagesList}>
              {messages.map(msg => (
                <li
                  key={msg._id}
                  className={`${styles.messageItem} ${
                    isVisitorMessage(msg)
                      ? styles.messageItemVisitor
                      : styles.messageItemOperator
                  }`}
                >
                  <MessageBubble msg={msg} styles={styles} />
                </li>
              ))}
            </ul>
          )}
        </Modal>
      </div>
    </div>
  );
};
